import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useModerationIntelligence } from '../hooks/useModerationIntelligence';
import { SignalScoreBar } from './SignalScoreBar';
import { formatDateTime } from '../lib/display';
import { formatDurationMs } from '../lib/formatDurationMs';
import {
  formatCoopScanStatus,
  isAwaitingCaseDelivery,
  normalizeDeliveryStatus,
} from '../lib/coopDelivery';
import { useTickingNow } from '../lib/useTickingNow';
import { CoopScanDetails } from './CoopScanDetails';
import {
  isTipmiHidePending,
  isTipmiHoldPending,
  isTipmiPublishPending,
} from '../lib/coopTipmiSync';
import {
  formatCoopScanBadge,
  isDeepScanSettled,
  isNoActionLike,
  isProvisionalNoAction,
} from '../lib/verdictDisplay';
import type { ModerationIntelligence } from '../types';

const VERDICT_STYLE: Record<string, string> = {
  APPROVED: 'bg-green-100 text-green-800 border-green-200',
  APPROVE: 'bg-green-100 text-green-800 border-green-200',
  BYPASSED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
  REJECT: 'bg-red-100 text-red-800 border-red-200',
  ESCALATED: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  ESCALATE: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  HUMAN_REVIEW: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  PENDING: 'bg-muted text-muted-foreground',
};

const IN_PROGRESS_STYLE = 'bg-sky-100 text-sky-800 border-sky-200';

function verdictClass(verdict: string | null, provisional = false): string {
  if (provisional) return IN_PROGRESS_STYLE;
  if (!verdict) return VERDICT_STYLE.PENDING ?? 'bg-muted text-muted-foreground';
  const upper = verdict.toUpperCase();
  for (const [key, cls] of Object.entries(VERDICT_STYLE)) {
    if (upper.includes(key)) return cls;
  }
  return 'bg-muted text-muted-foreground';
}

function analysisDepthLabel(steps: { stage: string }[]): string {
  const stageNames = steps.map((step) => step.stage.toLowerCase());
  const hasStage = (name: string) => stageNames.some((stage) => stage.includes(name));
  const stages = [
    hasStage('heuristic') ? 'heuristic' : null,
    hasStage('standard') || hasStage('gpu') ? 'standard GPU' : null,
    hasStage('deep') || hasStage('frontier') ? 'frontier' : null,
    hasStage('escalat') || hasStage('human') ? 'human review' : null,
  ].filter((stage): stage is string => stage !== null);
  return stages.length > 0
    ? stages.join(' → ')
    : `${steps.length} pipeline stage${steps.length === 1 ? '' : 's'}`;
}

function StateSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-36" />
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-6 w-24 rounded-md" />
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-2 w-4/5" />
        <Skeleton className="h-2 w-3/5" />
      </CardContent>
    </Card>
  );
}

export interface ModerationStateCardProps {
  postId?: number | undefined;
  userId?: number | undefined;
  caseId?: string | undefined;
  isLoading?: boolean | undefined;
  /** When provided (content detail), skip report-scoped intelligence fetch. */
  intelligence?: ModerationIntelligence | null | undefined;
  /** TipMi publish status — used to flag Rejected + still held/Published, or Approved + still held. */
  postStatus?: string | null | undefined;
  /** TipMi publication ledger — PENDING means a Coop case may still be en route. */
  moderationStatus?: string | null | undefined;
  /** When true, skip verdict_reason (e.g. content detail already shows AI summary). */
  hideVerdictReason?: boolean | undefined;
}

/**
 * Shows the current Coop scan state of a piece of content:
 * verdict badge, risk scores, analysis depth, classification, and last action.
 */
export function ModerationStateCard({
  postId,
  caseId,
  isLoading: externalLoading,
  intelligence: intelligenceProp,
  postStatus,
  moderationStatus,
  hideVerdictReason = false,
}: ModerationStateCardProps) {
  const useProp = intelligenceProp !== undefined;
  // Report detail still fetches by case/report id when intelligence is not passed in.
  const reportId = caseId ?? String(postId ?? '');
  const { intelligence: fetched, isLoading: fetchLoading } = useModerationIntelligence(
    useProp ? '' : reportId,
  );
  const intelligence = useProp ? intelligenceProp : fetched;
  const isLoading = externalLoading || (!useProp && fetchLoading);

  const awaitingDelivery = isAwaitingCaseDelivery(
    intelligence?.delivery_status,
    intelligence?.case_status,
  );
  const nowMs = useTickingNow(awaitingDelivery);

  if (isLoading) return <StateSkeleton />;
  const ledgerPending =
    (moderationStatus ?? '').trim().toUpperCase() === 'PENDING' && !intelligence;
  const deliveryFailed =
    normalizeDeliveryStatus(intelligence?.delivery_status) === 'failed' &&
    !intelligence?.verdict &&
    !(intelligence?.signals?.length);

  if (!intelligence || awaitingDelivery || ledgerPending || deliveryFailed) {
    // Only fabricate Opening from a real outbox stub (HOLD/LIVE_ENFORCE).
    // OBSERVE leaves ledger PENDING with no case/outbox — do not imply delivery.
    const coopStatus = formatCoopScanStatus({
      deliveryStatus: intelligence?.delivery_status ?? null,
      deliveryLastError: intelligence?.delivery_last_error ?? intelligence?.verdict_reason,
      deliveryLastStage: intelligence?.delivery_last_stage,
      deliveryAttemptCount: intelligence?.delivery_attempt_count,
      deliveryLastAttemptAt: intelligence?.delivery_last_attempt_at,
      deliveryNextAttemptAt: intelligence?.delivery_next_attempt_at,
      nowMs,
    });
    const label =
      coopStatus.phase !== 'none'
        ? coopStatus.label
        : deliveryFailed
          ? 'Coop · Delivery failed'
          : awaitingDelivery
            ? 'Coop · Opening'
            : 'No Coop case';
    const secondary =
      coopStatus.details.length > 0
        ? null
        : ledgerPending
          ? 'TipMi ledger is PENDING (observe / no Coop case on create). No media-manager case was opened.'
          : awaitingDelivery
            ? 'Waiting for media-manager case.'
            : 'Scan not started or unavailable for this content.';
    const awaitingStyle = awaitingDelivery;
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm">Coop scan</CardTitle>
          <Badge
            variant="outline"
            className={cn(
              'text-xs',
              awaitingStyle
                ? IN_PROGRESS_STYLE
                : deliveryFailed
                  ? 'bg-red-100 text-red-800 border-red-200'
                  : 'border-dashed text-muted-foreground',
            )}
          >
            {label}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-2">
          <CoopScanDetails details={coopStatus.details} />
          {secondary ? (
            <p className="text-xs text-muted-foreground break-words">{secondary}</p>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  const tipmiLedgerPending = (moderationStatus ?? '').trim().toUpperCase() === 'PENDING';
  const deepScanSettled = isDeepScanSettled({
    deepScanDurationMs: intelligence.deep_scan_duration_ms,
    deliveryStatus: intelligence.delivery_status,
    vectors: intelligence.vectors,
  });
  const provisional = isProvisionalNoAction(intelligence.verdict, intelligence.case_status, {
    deepScanSettled,
  });
  // TipMi sync warnings use harness verdict vs TipMi visibility — not shown as Coop badge.
  const hidePending = isTipmiHidePending(intelligence.verdict, postStatus);
  const holdPending = isTipmiHoldPending(intelligence.verdict, postStatus);
  const publishPending = isTipmiPublishPending(intelligence.verdict, postStatus);
  // The Coop payload omits absent collections, and `pipeline_steps` is not on the admin DTO at all
  // — default every array before reading it (same idiom as ModerationIntelligenceCard).
  const signals = intelligence.signals ?? [];
  const pipelineSteps = intelligence.pipeline_steps ?? [];
  const enforcementActions = intelligence.enforcement_actions ?? [];
  const enforcementPreviews = intelligence.enforcement_previews ?? [];
  const lastAction =
    enforcementActions[enforcementActions.length - 1] ??
    enforcementPreviews[enforcementPreviews.length - 1];

  // Extract key risk scores from signals
  const riskSignals = signals.filter((s) =>
    ['combined_risk', 'visual', 'text', 'text_score', 'visual_score'].some(
      (k) => s.signal_id.toLowerCase().includes(k) || s.display_name.toLowerCase().includes(k),
    ),
  );
  // Fallback: show top 3 scores if no explicit risk signals
  const displaySignals = riskSignals.length > 0 ? riskSignals : signals.slice(0, 3);

  const verdictBadgeLabel = formatCoopScanBadge({
    caseStatus: intelligence.case_status,
    deepScanSettled,
    enforcementActions,
    enforcementPreviews,
    verdict: intelligence.verdict,
  });
  const coopBadgeProvisional =
    provisional || verdictBadgeLabel === 'Coop · Scanning';

  const tipmiDecidePending =
    !hidePending &&
    !holdPending &&
    !publishPending &&
    !provisional &&
    deepScanSettled &&
    tipmiLedgerPending &&
    isNoActionLike(intelligence.verdict);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Coop scan</CardTitle>
        <Badge
          variant="outline"
          className={cn('text-xs', verdictClass(verdictBadgeLabel, coopBadgeProvisional))}
        >
          {verdictBadgeLabel}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        {provisional && (
          <p className="rounded border border-sky-200 bg-sky-50 px-2 py-1.5 text-xs text-sky-900">
            Coop · Scanning — media-manager AI scan is still running. No final Coop action yet.
          </p>
        )}
        {tipmiDecidePending && (
          <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
            Coop recorded no enforcement action, but TipMi publication is still pending a decide
            (Waiting / Processing). List or Force Approve if the post should go live.
          </p>
        )}
        {hidePending && (
          <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
            TipMi rule preview says Rejected, but TipMi is still not hidden (Waiting / Published). Hide
            has not been applied yet — use Take Down or Force Reject if needed.
          </p>
        )}
        {holdPending && (
          <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
            TipMi rule preview says Escalated, but TipMi is still Published. Hold (Waiting) has not
            been applied yet — this is not a live Coop action.
          </p>
        )}
        {publishPending && (
          <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
            TipMi rule preview says Approved, but TipMi is still held (Waiting — not published).
            Publish has not been applied yet — use List / Force Approve if needed.
          </p>
        )}

        {/* Risk Scores */}
        {displaySignals.length > 0 && (
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground">Risk Scores</span>
            {displaySignals.map((s) => (
              <SignalScoreBar
                key={s.signal_id}
                label={s.display_name}
                score={s.score}
                threshold={s.threshold}
              />
            ))}
          </div>
        )}

        {/* Coop deep scan wall time (GPU / Gemma+CoPE) */}
        {intelligence.deep_scan_duration_ms != null && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Deep scan time</span>
            <span className="text-xs font-medium">
              {formatDurationMs(intelligence.deep_scan_duration_ms)}
            </span>
          </div>
        )}

        {/* Analysis Depth */}
        {pipelineSteps.length > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Analysis Depth</span>
            <span className="text-xs font-medium">{analysisDepthLabel(pipelineSteps)}</span>
          </div>
        )}

        {/* Vectors / Classification */}
        {intelligence.vectors && Object.keys(intelligence.vectors).length > 0 && (
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Classification</span>
            <div className="flex flex-wrap gap-1">
              {Object.entries(intelligence.vectors).map(([category, scores]) => (
                <Badge key={category} variant="outline" className="text-[10px]">
                  {category}: {Object.keys(scores).join(', ')}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Verdict Reason */}
        {!hideVerdictReason && intelligence.verdict_reason && (
          <p className="text-xs text-muted-foreground">{intelligence.verdict_reason}</p>
        )}

        {/* Last Action */}
        {lastAction && (
          <div className="flex items-center justify-between border-t pt-2">
            <span className="text-xs text-muted-foreground">Last Action</span>
            <span className="text-xs">
              {lastAction.action}{' '}
              {lastAction.executed_at ? `— ${formatDateTime(lastAction.executed_at)}` : ''}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
